/**
 * Fonte única de verdade das URLs do site, metadados editoriais e schema.
 * Datas editoriais refletem o histórico real do repositório — nunca a data do build.
 */

export const SITE_URL = 'https://minimental.com.br';
export const SITE_URL_ES = 'https://minimental.org';

export const SPANISH_PAGES = [
  { path: '/es', locale: 'es', title: 'Mini Mental en español: guía del MMSE para profesionales', description: 'Información en español sobre el Mini Examen del Estado Mental (MMSE): aplicación, puntuación e interpretación responsable para profesionales de salud.', breadcrumb: 'Mini Mental en español', heading: 'Mini Mental en español: guía del MMSE', publishedAt: '2026-11-02', lastModified: '2026-11-03', lastReviewed: '2026-11-03' },
  { path: '/es/test-mini-mental-online', locale: 'es', title: 'Test Mini Mental online: guía para profesionales', description: 'Qué tener en cuenta al aplicar y registrar el Mini Mental online. Información educativa para profesionales; la puntuación no es un diagnóstico.', breadcrumb: 'Test Mini Mental online', heading: 'Test Mini Mental online: qué tener en cuenta', publishedAt: '2026-11-02', lastModified: '2026-11-03', lastReviewed: '2026-11-03' },
  { path: '/es/mini-examen-del-estado-mental', locale: 'es', title: 'Mini Examen del Estado Mental (MMSE): qué evalúa', description: 'Qué es el Mini Examen del Estado Mental, qué áreas explora y cómo comprender sus límites según idioma, escolaridad y contexto.', breadcrumb: 'Mini Examen del Estado Mental', heading: 'Mini Examen del Estado Mental: guía del MMSE', publishedAt: '2026-11-02', lastModified: '2026-11-03', lastReviewed: '2026-11-03' },
  { path: '/es/calculadora-mini-mental', locale: 'es', title: 'Mini Mental: puntaje, puntuación y resultados', description: 'Qué significan el puntaje o la puntuación del Mini Mental. La calculadora disponible usa la versión brasileña en portugués; no ofrece normas validadas en español.', breadcrumb: 'Calculadora Mini Mental', heading: 'Puntaje y puntuación del Mini Mental: resultados y límites', publishedAt: '2026-11-02', lastModified: '2026-11-03', lastReviewed: '2026-11-03' },
  { path: '/es/acerca-de', locale: 'es', type: 'institutional', title: 'Acerca de MiniMental.org: autoría y revisión editorial', description: 'Conoce quién escribe y revisa MiniMental.org, sus criterios editoriales y los límites de sus herramientas educativas.', breadcrumb: 'Acerca de', heading: 'Acerca de MiniMental.org', publishedAt: '2026-11-03', lastModified: '2026-11-03', lastReviewed: '2026-11-03' },
  { path: '/es/politica-de-privacidad', locale: 'es', type: 'institutional', title: 'Política de privacidad — MiniMental.org', description: 'Cómo MiniMental.org trata los datos de la calculadora pública, las cuentas profesionales, el correo y las métricas de uso.', breadcrumb: 'Política de privacidad', heading: 'Política de privacidad', publishedAt: '2026-11-03', lastModified: '2026-11-03', lastReviewed: '2026-11-03' },
  { path: '/es/terminos-de-uso', locale: 'es', type: 'institutional', title: 'Términos de uso — MiniMental.org', description: 'Condiciones de uso de MiniMental.org: calculadora, MiniMental PRO, responsabilidad profesional, MMSE y límites clínicos.', breadcrumb: 'Términos de uso', heading: 'Términos de uso', publishedAt: '2026-11-03', lastModified: '2026-11-03', lastReviewed: '2026-11-03' },
  { path: '/es/ayuda', locale: 'es', type: 'institutional', noindex: true, title: 'Ayuda y contacto — MiniMental.org', description: 'Cómo contactar con el equipo de MiniMental.org y compartir un diagnóstico técnico sin datos clínicos.', breadcrumb: 'Ayuda', heading: 'Ayuda y contacto', publishedAt: '2026-11-03', lastModified: '2026-11-03', lastReviewed: '2026-11-03' },
];

// Pares de páginas cuyo tema y propósito editorial corresponden entre idiomas.
// Cada sitemap publica estas relaciones en ambas direcciones.
export const LANGUAGE_PAGE_PAIRS = [
  ['/', '/es'],
  ['/como-aplicar', '/es/test-mini-mental-online'],
  ['/mini-exame-do-estado-mental', '/es/mini-examen-del-estado-mental'],
  ['/interpretacao-dos-resultados', '/es/calculadora-mini-mental'],
  ['/sobre', '/es/acerca-de'],
  ['/politica-de-privacidade', '/es/politica-de-privacidad'],
  ['/termos-de-uso', '/es/terminos-de-uso'],
];

export function getLanguagePagePair(path) {
  return LANGUAGE_PAGE_PAIRS.find(([ptPath, esPath]) => ptPath === path || esPath === path) || null;
}

const HOME_PUBLISHED = '2026-07-10';
const CORE_PUBLISHED = '2026-07-30';
const FANOUT_PUBLISHED = '2026-08-05';
const REVIEW_2026_08_05 = '2026-08-05';
const REVIEW_2026_08_21 = '2026-08-21';

export function formatEditorialDate(isoDate) {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
}

export function addMonthsIso(isoDate, months = 6) {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() + months);
  return date.toISOString().slice(0, 10);
}

export const HOME = {
  path: '/',
  title: 'Mini Mental Online: calculadora para pontuar o teste MEEM',
  description:
    'Calculadora online do Mini Exame do Estado Mental (MEEM) para profissionais: pontue o Mini Mental aplicado, consulte referências por escolaridade e interprete o resultado.',
  breadcrumb: 'Início',
  publishedAt: HOME_PUBLISHED,
  lastModified: '2026-11-03',
  lastReviewed: REVIEW_2026_08_21
};

export const GUIAS_PAGE = {
  path: '/guias',
  title: 'Guias do Mini Mental: aplicar, interpretar e acompanhar',
  description:
    'Encontre os guias do Mini Mental por tarefa: como aplicar, interpretar o resultado, considerar escolaridade, acompanhar mudanças e comparar instrumentos.',
  breadcrumb: 'Guias do MEEM',
  heading: 'Guias do Mini Mental (MEEM)',
  publishedAt: '2026-08-14',
  lastModified: REVIEW_2026_08_21,
  lastReviewed: REVIEW_2026_08_21
};

export const SUPPORT_PAGES = [
  {
    path: '/metodologia-da-calculadora',
    title: 'Metodologia da calculadora do Mini Mental: fontes e limites',
    description:
      'Veja de onde vêm os números da calculadora do MiniMental.com.br, como as referências brasileiras são usadas e quais são os limites da ferramenta.',
    breadcrumb: 'Metodologia da calculadora',
    heading: 'Metodologia da calculadora do Mini Mental',
    publishedAt: '2026-08-14',
    lastModified: '2026-08-14',
    lastReviewed: '2026-08-14'
  },
  {
    path: '/evidencia-brasileira',
    title: 'Evidência brasileira do MEEM: Brucki, Bertolucci e Lourenço',
    description:
      'Entenda as principais referências brasileiras do MEEM, suas populações, resultados, diferenças e limitações para interpretação clínica.',
    breadcrumb: 'Evidência brasileira',
    heading: 'Evidência brasileira do Mini Mental',
    publishedAt: '2026-08-14',
    lastModified: '2026-10-07',
    lastReviewed: '2026-08-14'
  },
  {
    path: '/suporte',
    title: 'Suporte e diagnóstico — MiniMental.com.br',
    description:
      'Verifique se uma dificuldade de acesso ao MiniMental vem da aplicação ou de uma proteção de rede e envie um diagnóstico técnico sanitizado.',
    breadcrumb: 'Suporte',
    heading: 'Suporte e diagnóstico do MiniMental',
    publishedAt: '2026-09-05',
    lastModified: '2026-09-05',
    lastReviewed: '2026-09-05',
    schemaType: 'support',
    noindex: true
  }
];

export const PAGES = [
  {
    path: '/escolaridade',
    title: 'Mini Mental por escolaridade: tabela de pontuação e cortes',
    description:
      'Como a escolaridade altera o Mini Mental: as medianas de Brucki et al. (2003), a referência de Lourenço e Veras (2006) e por que mediana não é ponto de corte.',
    breadcrumb: 'Escolaridade',
    heading: 'Mini Mental por escolaridade: como interpretar',
    publishedAt: CORE_PUBLISHED,
    lastModified: REVIEW_2026_08_21,
    lastReviewed: REVIEW_2026_08_21
  },
  {
    path: '/pontos-de-corte',
    title: 'Mini Mental: pontos de corte e pontuação por escolaridade',
    description:
      'Veja a pontuação do Mini Mental por escolaridade e compare os pontos de corte estudados por Brucki, Bertolucci e Lourenço e Veras. Entenda por que mediana não é corte diagnóstico.',
    breadcrumb: 'Pontos de corte',
    heading: 'Qual é o ponto de corte do Mini Mental?',
    publishedAt: '2026-08-14',
    lastModified: '2026-11-03',
    lastReviewed: REVIEW_2026_08_21
  },
  {
    path: '/interpretacao-dos-resultados',
    title: 'Pontuação do Mini Mental: como interpretar o resultado',
    description:
      'Entenda a pontuação e o resultado do Mini Mental (MEEM): referência, escolaridade, o que significa um escore alterado e por que o número sozinho não diagnostica.',
    breadcrumb: 'Pontuação e interpretação',
    heading: 'Pontuação do Mini Mental: como interpretar o resultado',
    publishedAt: '2026-08-14',
    lastModified: REVIEW_2026_08_21,
    lastReviewed: REVIEW_2026_08_21
  },
  {
    path: '/mini-mental-ou-moca',
    title: 'Mini Mental ou MoCA: diferenças e quando usar cada um',
    description:
      'Mini Mental ou MoCA: o que cada instrumento avalia, por que o MoCA detecta melhor o comprometimento leve e por que os dois escores não são intercambiáveis.',
    breadcrumb: 'Mini Mental ou MoCA',
    heading: 'Mini Mental ou MoCA: qual é a diferença?',
    publishedAt: CORE_PUBLISHED,
    lastModified: REVIEW_2026_08_05,
    lastReviewed: REVIEW_2026_08_05
  },
  {
    path: '/quem-pode-aplicar',
    title: 'Quem pode aplicar o Mini Mental? Requisitos e limites',
    description:
      'Quem pode aplicar o Mini Mental no Brasil, o que o treinamento na aplicação padronizada exige e por que aplicar e interpretar o MEEM não são a mesma coisa.',
    breadcrumb: 'Quem pode aplicar',
    heading: 'Quem pode aplicar o Mini Mental?',
    publishedAt: CORE_PUBLISHED,
    lastModified: '2026-08-14',
    lastReviewed: '2026-08-14'
  },
  {
    path: '/diagnostica-alzheimer',
    title: 'O Mini Mental diagnostica Alzheimer? O que o MEEM mostra',
    description:
      'O Mini Mental não diagnostica Alzheimer. Entenda o que o MEEM estabelece, o que o diagnóstico exige e quais causas de pontuação baixa não são demência.',
    breadcrumb: 'Diagnostica Alzheimer',
    heading: 'O Mini Mental diagnostica Alzheimer?',
    publishedAt: CORE_PUBLISHED,
    lastModified: '2026-08-14',
    lastReviewed: '2026-08-14'
  },
  {
    path: '/meem-incompleto',
    title: 'Mini Mental incompleto: como registrar e interpretar',
    description:
      'A aplicação do MEEM parou no meio? Veja como registrar os itens não avaliados no prontuário e por que o total de uma aplicação incompleta pode estar subestimado.',
    breadcrumb: 'MEEM incompleto',
    heading: 'Mini Mental incompleto: como registrar e interpretar',
    publishedAt: '2026-10-10',
    lastModified: '2026-10-10',
    lastReviewed: '2026-10-10'
  }
];

export const SEO_PAGES = [
  {
    path: '/mini-exame-do-estado-mental',
    title: 'Mini Exame do Estado Mental (MEEM): o que é e como funciona',
    description:
      'Entenda o Mini Exame do Estado Mental (MEEM), ou Mini Mental: finalidade, domínios, pontuação, aplicação, limites e a referência de Brucki et al. (2003).',
    breadcrumb: 'Mini Exame do Estado Mental',
    heading: 'Mini Exame do Estado Mental (MEEM): o que é e como funciona',
    publishedAt: REVIEW_2026_08_21,
    lastModified: REVIEW_2026_08_21,
    lastReviewed: REVIEW_2026_08_21
  },
  {
    path: '/mini-mental-pdf',
    title: 'Mini Mental PDF: ficha para imprimir e artigo original',
    description:
      'Imprima uma ficha em branco para registrar o Mini Mental (MEEM) aplicado. Acesse também o artigo original de Brucki et al. (2003), medianas por escolaridade e orientações de uso.',
    breadcrumb: 'Mini Mental PDF',
    heading: 'Mini Mental PDF para imprimir: ficha de registro e versão brasileira',
    publishedAt: REVIEW_2026_08_21,
    lastModified: REVIEW_2026_08_21,
    lastReviewed: REVIEW_2026_08_21
  },
  {
    path: '/pentagonos-mini-mental',
    title: 'Pentágonos no Mini Mental: desenho, avaliação e pontuação',
    description:
      'O que a tarefa dos pentágonos avalia no Mini Mental (MEEM), como pensar a pontuação e por que este site não reproduz o estímulo protegido.',
    breadcrumb: 'Pentágonos do Mini Mental',
    heading: 'Pentágonos no Mini Mental: o que o desenho avalia',
    publishedAt: REVIEW_2026_08_21,
    lastModified: REVIEW_2026_08_21,
    lastReviewed: REVIEW_2026_08_21
  }
];

export const FANOUT_PAGES = [
  {
    path: '/mini-mental-telessaude',
    title: 'Mini Mental por telessaúde: limites e como aplicar',
    description:
      'O Mini Mental pode ser aplicado por telessaúde? Veja o que muda na modalidade remota, os limites de comparação com a aplicação presencial e os direitos de uso.',
    breadcrumb: 'Mini Mental por telessaúde',
    heading: 'Mini Mental por telessaúde: o que muda na aplicação remota',
    publishedAt: '2026-10-05',
    lastModified: '2026-10-05',
    lastReviewed: '2026-10-05'
  },
  {
    path: '/como-aplicar',
    title: 'Como aplicar o Mini Mental: passo a passo para profissionais',
    description:
      'Etapas gerais para aplicar o Mini Mental com validade clínica: ambiente, ordem das tarefas, registro literal das respostas e pontuação, sem o roteiro protegido.',
    breadcrumb: 'Como aplicar',
    heading: 'Como aplicar o Mini Mental: passo a passo para profissionais',
    publishedAt: FANOUT_PUBLISHED,
    lastModified: '2026-08-14',
    lastReviewed: '2026-08-14'
  },
  {
    path: '/erros-de-aplicacao',
    title: 'Erros comuns na aplicação do Mini Mental (e como evitá-los)',
    description:
      'Erros mais frequentes na aplicação do Mini Mental: pontuar tentativas extras, dar pistas não previstas, mudar a ordem das tarefas e ignorar déficits sensoriais.',
    breadcrumb: 'Erros de aplicação',
    heading: 'Erros comuns na aplicação do Mini Mental (e como evitá-los)',
    publishedAt: FANOUT_PUBLISHED,
    lastModified: '2026-08-14',
    lastReviewed: '2026-08-14'
  },
  {
    path: '/formulario-e-direitos-de-uso',
    title: 'Existe PDF gratuito do Mini Mental? Direitos de uso',
    description:
      'Como obter o Mini Mental (MMSE) por canal autorizado, o que a PAR informa sobre reprodução e tradução e por que licença não é validade clínica.',
    breadcrumb: 'Formulário e direitos de uso',
    heading: 'Existe PDF ou formulário gratuito do Mini Mental? Direitos de uso',
    publishedAt: FANOUT_PUBLISHED,
    lastModified: '2026-08-14',
    lastReviewed: '2026-08-14'
  },
  {
    path: '/mini-mental-e-teste-do-relogio',
    title: 'Mini Mental e teste do relógio: por que aplicar os dois',
    description:
      'O Mini Mental e o teste do relógio se complementam: um cobre orientação, memória e linguagem; o outro, função executiva e organização visuoespacial.',
    breadcrumb: 'Mini Mental e teste do relógio',
    heading: 'Mini Mental e teste do relógio: por que aplicar os dois juntos',
    publishedAt: FANOUT_PUBLISHED,
    lastModified: FANOUT_PUBLISHED,
    lastReviewed: FANOUT_PUBLISHED
  },
  {
    path: '/acompanhamento-e-queda-de-pontos',
    title: 'Queda de pontos no Mini Mental: o que é significativo',
    description:
      'Não há um número universal de queda no Mini Mental que indique declínio. Veja variabilidade teste-reteste, efeito de aprendizagem e quando a mudança importa.',
    breadcrumb: 'Queda de pontos no acompanhamento',
    heading: 'Quantos pontos de queda no Mini Mental são significativos no acompanhamento?',
    publishedAt: FANOUT_PUBLISHED,
    lastModified: '2026-08-14',
    lastReviewed: '2026-08-14'
  },
  {
    path: '/adaptacoes-sensoriais',
    title: 'Mini Mental em pessoas com déficit visual, auditivo ou motor',
    description:
      'Como déficits visuais, auditivos e motores afetam a pontuação do Mini Mental, e quais adaptações práticas reduzem esse viés sem alterar o instrumento.',
    breadcrumb: 'Adaptações sensoriais',
    heading: 'Mini Mental em pessoas com déficit visual, auditivo ou motor',
    publishedAt: FANOUT_PUBLISHED,
    lastModified: '2026-08-14',
    lastReviewed: '2026-08-14'
  }
];

export const PRO_PAGE = {
  path: '/pro',
  title: 'MiniMental PRO — organize pacientes e avaliações',
  description:
    'Organize registros de pacientes e avaliações do Mini Mental numa área profissional com acesso pela sua conta. Conheça recursos, limites e planos do PRO.',
  breadcrumb: 'MiniMental PRO',
  heading: 'MiniMental PRO: pacientes e avaliações em um só lugar',
  publishedAt: '2026-09-02',
  lastModified: '2026-09-02',
  schemaType: 'software-application'
};

export const SOBRE_PAGE = {
  path: '/sobre',
  title: 'Sobre o MiniMental.com.br e a revisão editorial',
  description:
    'Quem escreve e revisa o conteúdo do MiniMental.com.br: autoria, CRM, política editorial, conflitos de interesse e como o site trata dados da calculadora.',
  breadcrumb: 'Sobre',
  heading: 'Sobre o MiniMental.com.br',
  publishedAt: FANOUT_PUBLISHED,
  lastModified: FANOUT_PUBLISHED,
  lastReviewed: FANOUT_PUBLISHED
};

export const LEGAL_PAGES = [
  {
    path: '/politica-de-privacidade',
    title: 'Política de Privacidade — MiniMental.com.br',
    description:
      'Como o MiniMental.com.br trata dados: a calculadora pública funciona localmente; o PRO autenticado armazena registros vinculados à conta.',
    breadcrumb: 'Política de Privacidade',
    publishedAt: HOME_PUBLISHED,
    lastModified: '2026-09-02'
  },
  {
    path: '/termos-de-uso',
    title: 'Termos de Uso — MiniMental.com.br',
    description:
      'Termos do MiniMental.com.br: calculadora pública, uso do PRO autenticado, dados registrados, responsabilidade profissional e limites da ferramenta.',
    breadcrumb: 'Termos de Uso',
    publishedAt: HOME_PUBLISHED,
    lastModified: '2026-09-02'
  }
];

/**
 * Páginas públicas de pontuação individual (/resultado/N-pontos): conteúdo editorial
 * próprio por pontuação, não gerado em massa. Publicadas aos poucos — ver
 * src/lib/scoreContent.js. `score` é o número usado pelo template para achar o conteúdo.
 */
const SCORE_PUBLISHED = '2026-10-10';
export const SCORE_PAGES = [
  {
    path: '/resultado/0-pontos',
    score: 0,
    title: 'MEEM 0/30: como interpretar essa pontuação?',
    description:
      'MEEM 0 de 30: por que confirmar que a aplicação foi completa vem antes de qualquer leitura cognitiva, e o que esse total indica por escolaridade e divisão de rastreio.',
    breadcrumb: '0 pontos',
    heading: 'MEEM 0/30: como interpretar essa pontuação?',
    publishedAt: SCORE_PUBLISHED,
    lastModified: SCORE_PUBLISHED,
    lastReviewed: SCORE_PUBLISHED
  },
  {
    path: '/resultado/10-pontos',
    score: 10,
    title: 'MEEM 10/30: como interpretar essa pontuação?',
    description:
      'MEEM 10 de 30: como essa pontuação se compara às medianas brasileiras e à divisão de rastreio de Lourenço e Veras (2006), e por que o número isolado não diferencia causas.',
    breadcrumb: '10 pontos',
    heading: 'MEEM 10/30: como interpretar essa pontuação?',
    publishedAt: SCORE_PUBLISHED,
    lastModified: SCORE_PUBLISHED,
    lastReviewed: SCORE_PUBLISHED
  },
  {
    path: '/resultado/18-pontos',
    score: 18,
    title: 'MEEM 18/30: como interpretar essa pontuação?',
    description:
      'MEEM 18 de 30: o limite exato da divisão de rastreio de Lourenço e Veras (2006) para quem não tem escolaridade formal, e por que a escolaridade muda totalmente a leitura.',
    breadcrumb: '18 pontos',
    heading: 'MEEM 18/30: como interpretar essa pontuação?',
    publishedAt: SCORE_PUBLISHED,
    lastModified: SCORE_PUBLISHED,
    lastReviewed: SCORE_PUBLISHED
  },
  {
    path: '/resultado/23-pontos',
    score: 23,
    title: 'MEEM 23/30: como interpretar essa pontuação?',
    description:
      'MEEM 23 de 30: por que a divisão de rastreio "geral" de Lourenço e Veras (2006) pode levar a leituras opostas da mesma pontuação, dependendo só da escolaridade.',
    breadcrumb: '23 pontos',
    heading: 'MEEM 23/30: como interpretar essa pontuação?',
    publishedAt: SCORE_PUBLISHED,
    lastModified: SCORE_PUBLISHED,
    lastReviewed: SCORE_PUBLISHED
  },
  {
    path: '/resultado/24-pontos',
    score: 24,
    title: 'MEEM 24/30: como interpretar essa pontuação?',
    description:
      'MEEM 24 de 30: o que essa pontuação indica por escolaridade, como ela se compara às medianas brasileiras e à divisão de rastreio de Lourenço e Veras (2006).',
    breadcrumb: '24 pontos',
    heading: 'MEEM 24/30: como interpretar essa pontuação?',
    publishedAt: SCORE_PUBLISHED,
    lastModified: SCORE_PUBLISHED,
    lastReviewed: SCORE_PUBLISHED
  },
  {
    path: '/resultado/27-pontos',
    score: 27,
    title: 'MEEM 27/30: como interpretar essa pontuação?',
    description:
      'MEEM 27 de 30: por que um total "alto" ainda pode ficar abaixo da mediana esperada para pessoas de maior escolaridade, e o que isso diz sobre o efeito teto do MEEM.',
    breadcrumb: '27 pontos',
    heading: 'MEEM 27/30: como interpretar essa pontuação?',
    publishedAt: SCORE_PUBLISHED,
    lastModified: SCORE_PUBLISHED,
    lastReviewed: SCORE_PUBLISHED
  },
  {
    path: '/resultado/30-pontos',
    score: 30,
    title: 'MEEM 30/30: como interpretar essa pontuação?',
    description:
      'MEEM 30 de 30, o teto do instrumento: por que um escore máximo não exclui comprometimento cognitivo leve, sobretudo em pessoas de alta escolaridade.',
    breadcrumb: '30 pontos',
    heading: 'MEEM 30/30: como interpretar essa pontuação?',
    publishedAt: SCORE_PUBLISHED,
    lastModified: SCORE_PUBLISHED,
    lastReviewed: SCORE_PUBLISHED
  }
];

/**
 * Páginas públicas por domínio (/dominio/:slug): o que cada um dos 7 domínios avalia,
 * grounded em DOMAINS_CONFIG e nos blocos/dados normativos de meemItems.js. Publicadas
 * aos poucos — ver src/lib/domainContent.js. `domainId` é o id de DOMAINS_CONFIG.
 */
const DOMAIN_PUBLISHED = '2026-10-10';
export const DOMAIN_PAGES = [
  {
    path: '/dominio/orientacao-temporal',
    domainId: 'temporalOrientation',
    title: 'Orientação temporal no Mini Mental: o que esse domínio avalia',
    description:
      'O que o bloco de orientação temporal do Mini Mental (MEEM) avalia, por que é o domínio com maior acerto entre pessoas saudáveis e o que uma perda aqui pode indicar.',
    breadcrumb: 'Orientação temporal',
    heading: 'Orientação temporal no Mini Mental: o que esse domínio avalia',
    publishedAt: DOMAIN_PUBLISHED,
    lastModified: DOMAIN_PUBLISHED,
    lastReviewed: DOMAIN_PUBLISHED
  },
  {
    path: '/dominio/orientacao-espacial',
    domainId: 'spatialOrientation',
    title: 'Orientação espacial no Mini Mental: o que esse domínio avalia',
    description:
      'O que o bloco de orientação espacial do Mini Mental (MEEM) avalia, do local específico ao estado, e por que o desempenho de pessoas saudáveis fica perto do teto aqui.',
    breadcrumb: 'Orientação espacial',
    heading: 'Orientação espacial no Mini Mental: o que esse domínio avalia',
    publishedAt: DOMAIN_PUBLISHED,
    lastModified: DOMAIN_PUBLISHED,
    lastReviewed: DOMAIN_PUBLISHED
  },
  {
    path: '/dominio/registro',
    domainId: 'registration',
    title: 'Registro no Mini Mental: o que esse domínio avalia',
    description:
      'O que o bloco de registro (memória imediata) do Mini Mental (MEEM) avalia, por que só a primeira repetição pontua e por que ele é a base da evocação.',
    breadcrumb: 'Registro',
    heading: 'Registro no Mini Mental: o que esse domínio avalia',
    publishedAt: DOMAIN_PUBLISHED,
    lastModified: DOMAIN_PUBLISHED,
    lastReviewed: DOMAIN_PUBLISHED
  },
  {
    path: '/dominio/atencao-e-calculo',
    domainId: 'attentionCalculation',
    title: 'Atenção e cálculo no Mini Mental: o que esse domínio avalia',
    description:
      'O que o bloco de atenção e cálculo (subtração seriada de 7) do Mini Mental (MEEM) avalia, e por que é o domínio mais sensível à escolaridade do teste.',
    breadcrumb: 'Atenção e cálculo',
    heading: 'Atenção e cálculo no Mini Mental: o que esse domínio avalia',
    publishedAt: DOMAIN_PUBLISHED,
    lastModified: DOMAIN_PUBLISHED,
    lastReviewed: DOMAIN_PUBLISHED
  },
  {
    path: '/dominio/evocacao',
    domainId: 'recall',
    title: 'Evocação no Mini Mental: o que esse domínio avalia',
    description:
      'O que o bloco de evocação do Mini Mental (MEEM) avalia, por que é o item mais sensível do teste e por que pessoas saudáveis também perdem ponto aqui.',
    breadcrumb: 'Evocação',
    heading: 'Evocação no Mini Mental: o que esse domínio avalia',
    publishedAt: DOMAIN_PUBLISHED,
    lastModified: DOMAIN_PUBLISHED,
    lastReviewed: DOMAIN_PUBLISHED
  },
  {
    path: '/dominio/linguagem',
    domainId: 'language',
    title: 'Linguagem no Mini Mental: o que esse domínio avalia',
    description:
      'O que as cinco tarefas de linguagem do Mini Mental (MEEM) avaliam — nomeação, repetição, comando, leitura e escrita — e por que são 8 dos 30 pontos do teste.',
    breadcrumb: 'Linguagem',
    heading: 'Linguagem no Mini Mental: o que esse domínio avalia',
    publishedAt: DOMAIN_PUBLISHED,
    lastModified: DOMAIN_PUBLISHED,
    lastReviewed: DOMAIN_PUBLISHED
  },
  {
    path: '/dominio/capacidade-visuoconstrutiva',
    domainId: 'visuoconstruction',
    title: 'Capacidade visuoconstrutiva no Mini Mental: o que esse domínio avalia',
    description:
      'O que a cópia do desenho geométrico do Mini Mental (MEEM) avalia, e por que é o item com a menor média de acerto entre pessoas saudáveis de toda a amostra normativa.',
    breadcrumb: 'Capacidade visuoconstrutiva',
    heading: 'Capacidade visuoconstrutiva no Mini Mental: o que esse domínio avalia',
    publishedAt: DOMAIN_PUBLISHED,
    lastModified: DOMAIN_PUBLISHED,
    lastReviewed: DOMAIN_PUBLISHED
  }
];

export const ALL_ROUTES = [HOME, GUIAS_PAGE, ...SUPPORT_PAGES, ...PAGES, ...SEO_PAGES, ...FANOUT_PAGES, ...SPANISH_PAGES, ...SCORE_PAGES, ...DOMAIN_PAGES, PRO_PAGE, SOBRE_PAGE, ...LEGAL_PAGES];

export function getPageByPath(path) {
  return ALL_ROUTES.find((page) => page.path === path);
}

export function canonicalFor(path, locale = 'pt') {
  const isSpanishPath = String(path) === '/es' || String(path).startsWith('/es/');
  const base = locale === 'es' || isSpanishPath ? SITE_URL_ES : SITE_URL;
  return path === '/' ? `${base}/` : `${base}${path}`;
}

export function buildSitemapXml(pages = ALL_ROUTES, { includeLanguageAlternates = false } = {}) {
  const pageByPath = new Map(ALL_ROUTES.map((page) => [page.path, page]));
  const pairByPath = new Map();
  LANGUAGE_PAGE_PAIRS.forEach(([ptPath, esPath]) => {
    pairByPath.set(ptPath, [ptPath, esPath]);
    pairByPath.set(esPath, [ptPath, esPath]);
  });
  const urls = pages.filter((page) => !page.noindex).map((page) => {
    const pair = includeLanguageAlternates ? pairByPath.get(page.path) : null;
    const alternates = pair?.map((path) => {
      const alternatePage = pageByPath.get(path);
      return alternatePage && !alternatePage.noindex
        ? `    <xhtml:link rel="alternate" hreflang="${alternatePage.locale === 'es' ? 'es' : 'pt-BR'}" href="${canonicalFor(path, alternatePage.locale || 'pt')}" />`
        : '';
    }).filter(Boolean) || [];
    if (pair) alternates.push(`    <xhtml:link rel="alternate" hreflang="x-default" href="${canonicalFor('/es', 'es')}" />`);
    return `  <url>\n    <loc>${canonicalFor(page.path, page.locale || 'pt')}</loc>\n    <lastmod>${page.lastModified}</lastmod>${alternates.length ? `\n${alternates.join('\n')}` : ''}\n  </url>`;
  }).join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">\n${urls}\n</urlset>\n`;
}

function breadcrumbItemsFor(page) {
  if (page.locale === 'es') {
    const home = { '@type': 'ListItem', position: 1, name: 'Inicio', item: canonicalFor('/es', 'es') };
    if (page.path === '/es') return [home];
    return [home, { '@type': 'ListItem', position: 2, name: page.breadcrumb, item: canonicalFor(page.path, 'es') }];
  }

  const home = { '@type': 'ListItem', position: 1, name: 'Início', item: `${SITE_URL}/` };
  const guides = { '@type': 'ListItem', position: 2, name: 'Guias do MEEM', item: `${SITE_URL}/guias` };

  if (page.path === '/guias') return [home, guides];

  const isGuideContent = [...SUPPORT_PAGES, ...PAGES, ...SEO_PAGES, ...FANOUT_PAGES, ...SCORE_PAGES, ...DOMAIN_PAGES].some((item) => item.path === page.path);
  if (isGuideContent) {
    return [
      home,
      guides,
      { '@type': 'ListItem', position: 3, name: page.breadcrumb, item: canonicalFor(page.path) }
    ];
  }

  return [
    home,
    { '@type': 'ListItem', position: 2, name: page.breadcrumb, item: canonicalFor(page.path) }
  ];
}

function websiteEntity() {
  return {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: `${SITE_URL}/`,
    name: 'MiniMental',
    alternateName: ['MiniMental.com.br', 'MiniMental.org'],
    sameAs: [`${SITE_URL}/`, `${SITE_URL_ES}/es`],
    inLanguage: ['pt-BR', 'es']
  };
}

function buildSupportPageJsonLd(page) {
  const url = canonicalFor(page.path, page.locale || 'pt');

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: page.title,
        description: page.description,
        inLanguage: page.locale === 'es' ? 'es' : 'pt-BR',
        isPartOf: { '@id': `${SITE_URL}/#website` },
        datePublished: page.publishedAt,
        dateModified: page.lastModified
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumbItemsFor(page)
      },
      websiteEntity()
    ]
  };
}

function buildProPageJsonLd(page) {
  const url = canonicalFor(page.path);
  const applicationId = `${url}#application`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: page.title,
        description: page.description,
        inLanguage: 'pt-BR',
        isPartOf: { '@id': `${SITE_URL}/#website` },
        mainEntity: { '@id': applicationId },
        datePublished: page.publishedAt,
        dateModified: page.lastModified
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumbItemsFor(page)
      },
      {
        '@type': 'SoftwareApplication',
        '@id': applicationId,
        name: 'MiniMental PRO',
        description: page.description,
        url,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: [
          { '@type': 'Offer', name: 'Founder LTD', price: '69.00', priceCurrency: 'BRL', url: `${url}#planos` }
        ]
      },
      websiteEntity()
    ]
  };
}

export function buildPageJsonLd(page) {
  if (page.schemaType === 'software-application') return buildProPageJsonLd(page);
  if (page.schemaType === 'support') return buildSupportPageJsonLd(page);

  const url = canonicalFor(page.path, page.locale || 'pt');

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': page.type === 'institutional' ? 'WebPage' : 'MedicalWebPage',
        '@id': `${url}#webpage`,
        url,
        name: page.title,
        description: page.description,
        inLanguage: page.locale === 'es' ? 'es' : 'pt-BR',
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: {
          '@type': 'MedicalTest',
          name: page.locale === 'es' ? 'Mini Examen del Estado Mental' : 'Mini Exame do Estado Mental',
          alternateName: page.locale === 'es' ? ['Mini Mental', 'MMSE'] : ['Mini Mental', 'MEEM', 'MMSE']
        },
        datePublished: page.publishedAt,
        dateModified: page.lastModified,
        lastReviewed: page.lastReviewed,
        author: {
          '@type': 'Person',
          '@id': `${SITE_URL}/sobre#autor`,
          name: 'Dr. Carlos Felipe',
          jobTitle: 'Médico',
          identifier: 'CRM-MG 105.721',
          image: `${SITE_URL}/dr-carlos-felipe.jpg`,
          sameAs: [`${SITE_URL}/sobre`, 'https://blog.eidoc.com.br', 'https://cadu.online']
        },
        reviewedBy: {
          '@type': 'Person',
          '@id': `${SITE_URL}/sobre#autor`,
          name: 'Dr. Carlos Felipe',
          jobTitle: 'Médico',
          identifier: 'CRM-MG 105.721',
          image: `${SITE_URL}/dr-carlos-felipe.jpg`,
          sameAs: [`${SITE_URL}/sobre`, 'https://blog.eidoc.com.br', 'https://cadu.online']
        }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumbItemsFor(page)
      },
      websiteEntity()
    ]
  };
}
